import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';

import Loader3D from '../../../components/Loader/Loader3D';
import apiRequest from '../../../api/request';
import DeveloperNote from './DeveloperNote';

/**
 * WHAT IS BEHIND ONE SALES NUMBER.
 *
 * Clicking the Sales cell on the OGD Point Tracker opens this: every course sold,
 * one row each, with the working shown rather than only the answer.
 *
 * ONE ROW PER COURSE, NOT PER STUDENT
 *
 * Sales counts courses. A student who bought three courses is three rows here but
 * one row in the Admissions popup. That is why an OGA's sale list is normally
 * longer than their admission count, and it is correct.
 *
 * EACH ROW IS TAKEN APART
 *
 *   Tuition        the amount AT THE TIME OF THE SALE, and where it came from:
 *                  "original" when the fee was later changed and the retention log
 *                  had to be read, or the record when it was never changed
 *   Admission fee  as stored, plus the rate when it was sold in dirhams
 *   Total          the two added
 *
 * A number nobody can take apart is a number nobody trusts.
 */

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** "2026-04-07" shown as "07 Apr 2026". */
const prettyDate = (raw) => {
  if (!raw) return '';
  const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(raw);

  const [, year, month, day] = match;
  return `${day} ${MONTHS[Number(month) - 1]} ${year}`;
};

const inr = (value) =>
  '₹ ' + Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

export default function SalesDetailDialog({
  open,
  onClose,
  person, // { userId, oga, team, sales }
  fromDate,
  toDate,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!open || !person?.userId) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      const res = await apiRequest({
        method: 'get',
        url:
          `ogd-point-tracker/sales?userId=${person.userId}` +
          `&fromDate=${fromDate}&toDate=${toDate}`,
      });
      if (cancelled) return;

      if (res?.status === 'success') {
        setRows(res.data?.rows || []);
        setMeta(res.data?.meta || null);
      } else {
        setError(res?.message || 'Could not load the sales');
      }
      setLoading(false);
    })();

    // a fast second click must not let the first answer overwrite the second
    return () => {
      cancelled = true;
    };
  }, [open, person?.userId, fromDate, toDate]);

  const listed = rows.reduce((sum, r) => sum + Number(r.total || 0), 0);

  // the cell said one figure and the lines add to another. Rounded to the rupee
  // first, because the cell is rounded for display
  const mismatch =
    !loading &&
    !error &&
    person?.sales != null &&
    Math.round(Number(person.sales)) !== Math.round(listed);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      // the box is nearly the whole window. These tables are wide and are
      // read side by side against the number on the cell, so scrolling a
      // narrow box was the slowest part of checking a figure.
      PaperProps={{ sx: { height: '92vh', maxHeight: '92vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Sales · {person?.oga}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {inr(listed)} · {rows.length} {rows.length === 1 ? 'course' : 'courses'}
              {meta?.students != null && ` · ${meta.students} students`}
              {person?.team ? ` · ${person.team}` : ''}
              {fromDate ? ` · ${fromDate} to ${toDate}` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Icon icon="mdi:close" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {loading && <Loader3D />}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && rows.length === 0 && (
          <Alert severity="info" icon={<Icon icon="mdi:tag-off-outline" />}>
            No sales in this period.
          </Alert>
        )}

        {mismatch && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The table shows {inr(person.sales)} for this period but these lines add up
            to {inr(listed)}. The two should always match.
          </Alert>
        )}

        {!loading && !error && rows.length > 0 && (
          <Stack spacing={2}>
            {/* the two halves of the figure, before the detail */}
            <Box>
              <Stack
                direction="row"
                spacing={3}
                divider={<Divider orientation="vertical" flexItem />}
                flexWrap="wrap"
                useFlexGap
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Tuition
                  </Typography>
                  <Typography variant="subtitle2">{inr(meta?.tuition)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Admission fees
                  </Typography>
                  <Typography variant="subtitle2">{inr(meta?.fee)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {inr(listed)}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {meta?.fromLog > 0 && (
              <Alert severity="info" icon={<Icon icon="mdi:history" />}>
                {meta.fromLog} of these had sessions added later, so the tuition shown
                is the <strong>original</strong> amount at the time of the sale. The
                extra belongs to the CRO who added it, not to the OGA.
              </Alert>
            )}

            {meta?.noRate > 0 && (
              <Alert severity="warning" icon={<Icon icon="mdi:currency-usd-off" />}>
                {meta.noRate} of these were sold in dirhams but no paired payment
                exists to work a rate from, so the fee is left as stored. Those lines
                are understated by roughly ₹575 each.
              </Alert>
            )}

            <TableContainer sx={{ maxHeight: 'calc(92vh - 190px)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 45 }}>#</TableCell>
                    <TableCell>Student</TableCell>
                    <TableCell sx={{ width: 110 }}>Admission no</TableCell>
                    <TableCell sx={{ width: 110 }} title="course_enrollment.course_enrollment_id">
                      Enrolment id
                    </TableCell>
                    <TableCell>Course</TableCell>
                    <TableCell
                      align="right"
                      sx={{ width: 160 }}
                      title="The tuition as it was WHEN THE SALE WAS MADE, not as it stands today"
                    >
                      Tuition
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ width: 150 }}
                      title="From cart_items. Converted to rupees when it was sold in dirhams."
                    >
                      Admission fee
                    </TableCell>
                    <TableCell align="right" sx={{ width: 130 }}>
                      Total
                    </TableCell>
                    <TableCell sx={{ width: 130 }}>First payment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, index) => (
                    <TableRow key={r.enrollmentId} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{r.name || ''}</TableCell>
                      <TableCell>{r.admnNo}</TableCell>
                      {/* the database key for this row, so a figure on screen
                          can be chased straight into course_enrollment */}
                      <TableCell>{r.enrollmentId}</TableCell>
                      <TableCell>{r.course || ''}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{inr(r.tuition)}</Typography>
                        {r.tuitionFrom === 'log' && (
                          // the fee was changed later, so this is the original and
                          // the row says what it reads today
                          <Typography
                            variant="caption"
                            sx={{ color: '#9C27B0' }}
                            title={`Sessions were added later. It reads ${inr(
                              r.tuitionNow,
                            )} today, but that extra is the CRO's work.`}
                          >
                            original · now {inr(r.tuitionNow)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{inr(r.fee)}</Typography>
                        {r.inDirhams && r.feeAsStored > 0 && (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                            alignItems="center"
                          >
                            <Typography variant="caption" color="text.secondary">
                              {r.feeAsStored} AED
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={r.rateFound ? 'info' : 'warning'}
                              label={
                                r.rateFound
                                  ? `× ${Number(r.rate).toFixed(2)}`
                                  : 'no rate'
                              }
                            />
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {inr(r.total)}
                        </Typography>
                      </TableCell>
                      <TableCell>{prettyDate(r.paidOn)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}

        <DeveloperNote
          title="Sales is the DEAL VALUE of each course, tuition plus admission fee, as it stood on the day of the sale"
          steps={[
            {
              what: 'Start with exactly the same enrolments Admissions uses',
              where: 'course_enrollment  →  owned by user.userID = ' + (person?.userId ?? '') + ', status <> -1, first real payment inside the month',
              why: 'Sales has to cover the same enrolments as Admissions or the two stop agreeing',
            },
            {
              what: 'TUITION, first choice: the amount before anything was added',
              where: 'course_retention_log  WHERE course_enrollment_id = the enrolment  →  EARLIEST row by created_at  →  sale_amount_before',
              why: 'the log keeps every change, so its earliest row holds the original sale amount',
            },
            {
              what: 'TUITION, fallback when the enrolment has no log row at all',
              where: 'course_enrollment.cro_assignment_id  →  cro_assignment.sro_remarks_total_fees',
              why: 'no log row means the fee was never changed, so this column is still the original. It is OVERWRITTEN when sessions are added, which is why it is only the fallback',
            },
            {
              what: 'ADMISSION FEE',
              where: 'cart_items.cart_item_course_enrollment_id  =  course_enrollment.course_enrollment_id  →  cart_item_admission_fee',
              why: 'NEVER from cro_assignment. Its sro_remarks_admission_fee_status is written once and goes stale',
            },
            {
              what: 'Convert the fee if it is not in rupees',
              where: 'cart_items.cart_item_selected_option_currency = 2 means DIRHAMS  →  rate from income.income_amount / income.income_amount_aed on that same enrolment',
              why: 'there is no exchange rate table. With no paired payment the fee is left as stored, which understates a Gulf sale',
            },
            {
              what: 'Add up every course line',
              where: 'SUM(tuition + admission fee)   =   Sales',
              why: 'one line per COURSE, so a student buying three courses is 1 admission and 3 sale lines',
            },
          ]}
          lines={[
            'The extra from added sessions is dropped on purpose. The OGA is credited for bringing the student in, and extending an enrolled student is the CRO work.',
            'IF course_retention_log IS NOT ON LIVE, every row falls back to sro_remarks_total_fees and a closed month can still move when sessions are added.',
          ]}
        />
      </DialogContent>
    </Dialog>
  );
}
